import { send } from '@actual-app/core/platform/client/connection';
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

import { borrarPin, tienePin } from './pin';

/**
 * Entrar con huella (llaves de acceso). Este móvil recuerda qué llave suya
 * dio de alta, para enseñar el botón y para «Pedir huella al abrir».
 */
const CLAVE_ID = 'rumbo-huella-id';
const CLAVE_AL_ABRIR = 'rumbo-huella-al-abrir';

function leer(clave: string): string | null {
  try {
    return window.localStorage.getItem(clave);
  } catch {
    return null;
  }
}

function escribir(clave: string, valor: string | null) {
  try {
    if (valor === null) {
      window.localStorage.removeItem(clave);
    } else {
      window.localStorage.setItem(clave, valor);
    }
  } catch {
    // Sin almacenamiento (modo privado): la huella no se recuerda aquí.
  }
}

export function puedeUsarHuella(): boolean {
  return browserSupportsWebAuthn();
}

export function huellaDeEsteMovil(): string | null {
  return leer(CLAVE_ID);
}

/** Sin PIN no hay bloqueo: sin él, quien no pueda usar la huella no entra. */
export function pedirHuellaAlAbrir(): boolean {
  return (
    leer(CLAVE_AL_ABRIR) === '1' && huellaDeEsteMovil() !== null && tienePin()
  );
}

/** Al apagarlo se borra también el PIN, que solo sirve para el bloqueo. */
export function cambiarPedirHuellaAlAbrir(activo: boolean) {
  escribir(CLAVE_AL_ABRIR, activo ? '1' : null);
  if (!activo) {
    borrarPin();
  }
}

/** «Android», «iPhone»…: el nombre con el que se ve en la lista. */
export function nombreDeEsteAparato(ua = navigator.userAgent): string {
  if (/iPhone/.test(ua)) {
    return 'iPhone';
  }
  if (/iPad/.test(ua)) {
    return 'iPad';
  }
  if (/Android/.test(ua)) {
    return 'Android';
  }
  if (/Mac OS X/.test(ua)) {
    return 'Mac';
  }
  if (/Windows/.test(ua)) {
    return 'Windows';
  }
  return 'Linux';
}

export type ResultadoHuella =
  | 'ok'
  | 'cancelada'
  | 'sin-conexion'
  | 'no-valida'
  | 'demasiadas';

// El móvil lanza NotAllowedError si se cierra el diálogo o pasa el tiempo.
function esCancelada(error: unknown) {
  return error instanceof Error && error.name === 'NotAllowedError';
}

export async function activarHuella(): Promise<ResultadoHuella> {
  const opciones = await send('huella-opciones-alta');
  if (opciones.error || !opciones.opciones) {
    return opciones.error === 'demasiadas-huellas'
      ? 'demasiadas'
      : opciones.error === 'sin-conexion'
        ? 'sin-conexion'
        : 'no-valida';
  }
  let respuesta;
  try {
    respuesta = await startRegistration({
      optionsJSON:
        opciones.opciones as unknown as PublicKeyCredentialCreationOptionsJSON,
    });
  } catch (error) {
    return esCancelada(error) ? 'cancelada' : 'no-valida';
  }
  const alta = await send('huella-alta', {
    respuesta: respuesta as unknown as Record<string, unknown>,
    nombre: nombreDeEsteAparato(),
  });
  if (alta.error || !alta.id) {
    return alta.error === 'sin-conexion' ? 'sin-conexion' : 'no-valida';
  }
  escribir(CLAVE_ID, alta.id);
  return 'ok';
}

export async function desactivarHuella(): Promise<ResultadoHuella> {
  const id = huellaDeEsteMovil();
  if (id) {
    const res = await send('huella-borrar', { id });
    if (res.error === 'sin-conexion') {
      return 'sin-conexion';
    }
  }
  escribir(CLAVE_ID, null);
  cambiarPedirHuellaAlAbrir(false);
  return 'ok';
}

export async function entrarConHuella(): Promise<ResultadoHuella> {
  const opciones = await send('huella-opciones-entrada');
  if (opciones.error || !opciones.opciones) {
    return opciones.error === 'sin-conexion' ? 'sin-conexion' : 'no-valida';
  }
  let respuesta;
  try {
    respuesta = await startAuthentication({
      optionsJSON:
        opciones.opciones as unknown as PublicKeyCredentialRequestOptionsJSON,
    });
  } catch (error) {
    return esCancelada(error) ? 'cancelada' : 'no-valida';
  }
  const res = await send('huella-entrar', {
    respuesta: respuesta as unknown as Record<string, unknown>,
  });
  if (res.error) {
    return res.error === 'sin-conexion' ? 'sin-conexion' : 'no-valida';
  }
  // Si este móvil no la tenía apuntada (p. ej. tras borrar datos), lo hace.
  escribir(CLAVE_ID, respuesta.id);
  return 'ok';
}

/**
 * «Pedir huella al abrir»: solo comprueba en este móvil que es la persona
 * (no habla con el servidor, así funciona también sin conexión).
 */
export async function comprobarHuellaEnEsteMovil(): Promise<ResultadoHuella> {
  const id = huellaDeEsteMovil();
  if (!id) {
    return 'ok';
  }
  const reto = new Uint8Array(32);
  crypto.getRandomValues(reto);
  try {
    await startAuthentication({
      optionsJSON: {
        challenge: btoa(String.fromCharCode(...reto))
          .replace(/\+/g, '-')
          .replace(/\//g, '_')
          .replace(/=+$/, ''),
        rpId: window.location.hostname,
        allowCredentials: [{ id, type: 'public-key' }],
        userVerification: 'required',
        timeout: 60000,
      },
    });
    return 'ok';
  } catch (error) {
    return esCancelada(error) ? 'cancelada' : 'no-valida';
  }
}
